import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Copy, Eye, Pencil, Trash2 } from "lucide-react";

/**
 * The three-dot menu on a Discovery Offers card (spec 10.2.6, 10.4).
 *
 * Four actions: Reuse Offer, View Campaign, Edit Campaign, and Delete.
 *
 * **Delete is offered on genuinely ended campaigns only** (10.4.4), and "ended" is not
 * the same as "not active". derive_offer_status is binary - a PAUSED campaign yields
 * EXPIRED - which is right for serving and dangerous here: a merchant who paused a
 * campaign deliberately, and can resume it, would otherwise be handed a delete button
 * for offers they are about to use again. So this gates on the RAW campaign status,
 * and a paused campaign gets no Delete.
 *
 * An active campaign gets no Delete either: its offer is being served, and removing it
 * from a list page, away from its campaign, is not a decision to make behind a menu.
 */

// Statuses that mean the campaign is over and will not resume.
//
// Exactly the two that exist. This previously also listed CANCELLED and ARCHIVED, and
// EXPIRED itself was unreachable: CampaignStatus had no EXPIRED value at all, there is
// no CANCELLED, and archiving is a collection move rather than a status. So three of the
// four branches were dead and only COMPLETED ever matched.
//
// EXPIRED is now real (set by check_campaign_expiry when a campaign passes end_at). An
// archived campaign is deleted from `campaigns` outright, so its offers arrive with a
// null campaign_status - handled by the null branch below, not by a status name.
const ENDED_STATUSES = new Set(["EXPIRED", "COMPLETED"]);

export function isDeletable(offer) {
  const status = offer?.campaign_status;
  return status == null || ENDED_STATUSES.has(status);
}

export default function OfferActionMenu({ offer, onReuse, onViewCampaign, onEditCampaign, onDelete }) {
  const deletable = isDeletable(offer);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Offer actions">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => onReuse?.(offer)}>
          <Copy className="h-3.5 w-3.5 mr-2" />
          Reuse Offer
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onViewCampaign?.(offer)}>
          <Eye className="h-3.5 w-3.5 mr-2" />
          View Campaign
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEditCampaign?.(offer)}>
          <Pencil className="h-3.5 w-3.5 mr-2" />
          Edit Campaign
        </DropdownMenuItem>

        {deletable && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete?.(offer)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
